import { Box } from "@mui/material";
import { IconButton } from "@mui/material";
import { Typography } from "@mui/material";
import { Stack } from "@mui/material";
import { JSX } from "react";


interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
    appDims: any;
    compHeights: any;
    factor: number;
    /** Override the height subtracted from appUsableHeight. Defaults to 180 (accounts for tab button row). */
    heightOffset?: number;
    /** Fill parent flex height/width instead of using appUsableHeight math. */
    fillAvailable?: boolean;
  }

export function TabPanel(props: TabPanelProps) {
    const { children, value, index, appDims, compHeights, factor, heightOffset = 180, fillAvailable = false, ...other } = props;
  
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`tabpanel-${index}`}
        aria-labelledby={`tab-${index}`}
        style={{ 
            ...(fillAvailable
                ? {
                    flex: value === index ? 1 : undefined,
                    minHeight: 0,
                    height: value === index ? '100%' : undefined,
                    overflow: 'hidden',
                    display: value === index ? 'flex' : undefined,
                    flexDirection: 'column' as const,
                  }
                : {
                    height: compHeights.appUsableHeight - heightOffset * factor,
                    overflow: 'auto',
                  }),
            width: '100%',
            overscrollBehavior: 'none',
            boxSizing: 'border-box',
        }}
        {...other}
      >
        {value === index && (
          <Box sx={{
            padding: '10px 20px',
            height: fillAvailable ? '100%' : '94%',
            width: '100%',
            boxSizing: 'border-box',
            display: fillAvailable ? 'flex' : 'block',
            flexDirection: 'column',
            flex: fillAvailable ? 1 : undefined,
            minHeight: 0,
            overflow: fillAvailable ? 'hidden' : undefined,
          }}>
            {children as JSX.Element}
          </Box>
        )}
      </div>
    );
  }

  export function RoundTabPanel(props: TabPanelProps) {
    const { children, value, index, appDims, compHeights, factor, ...other } = props;
  
    return (
      <div
        role="round-tabpanel"
        hidden={value !== index}
        id={`round-tabpanel-${index}`}
        aria-labelledby={`round-tab-${index}`}
        style={{ 
            height: compHeights.appUsableHeight - 350*factor,
            width: '100%',
            overflowY: 'auto',
            /* border: '1px solid black', */
        }}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 0, flex: 1 }}>
            {children as JSX.Element}
          </Box>
        )}
      </div>
    );
  }

  interface TabButtonProps {
    index: number;
    label: string;
    factor: number;
    appTheme: any;
    tabValue: number;
    setTabValue: React.Dispatch<React.SetStateAction<number>>;
  }

  export function TabButton({index, label, factor, appTheme, tabValue, setTabValue}: TabButtonProps) {

    return (
        <IconButton
            onClick={() => setTabValue(index)}
            sx={{
                height: 40*factor, 
                width: 120*factor, 
                marginBottom: '2px',
                borderRadius: 2,
                backgroundColor: (tabValue === index) ? `${appTheme.palette.primary.extraLight}` : 'transparent',
                borderBottom: (tabValue === index) ? `2px solid ${appTheme.palette.primary.main}` : 'none',
                transition: 'all 200ms ease-in-out',
                '&:hover': { backgroundColor: (tabValue === index) ? `${appTheme.palette.primary.extraLight}` : 'rgba(0, 0, 0, 0.08)' },
            }}
            {...a11yProps(index)}>
            <Typography variant='subtitle2' color='primary.main'>{label}</Typography>
        </IconButton>
    );
  }

  function a11yProps(index: number) {
    return {
      id: `tab-button-${index}`,
      'aria-controls': `button-tabpanel-${index}`,      
    };
  }

interface RoundButtonProps {
    number: number;
    roundValue: number;
    appTheme: any;
    editMode: boolean;
    factor: number;
    setRoundValue: React.Dispatch<React.SetStateAction<number>>;
}

export function RoundButton({ number, roundValue, editMode, appTheme, factor, setRoundValue}: RoundButtonProps) {
  
  return (
        <IconButton 
            disabled={editMode}
            sx={{ 
                padding: 0,
                borderRadius: 15*factor, 
                width: 30*factor, 
                height: 30*factor, 
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: roundValue === number ? `${appTheme.palette.primary.light}` : 'lightgray',
                '&:disabled': {
                    backgroundColor: 'lightgray',
                }
            }}
            onClick={() => setRoundValue(number)}>
            <Stack direction={'row'} spacing={0} sx={{ alignItems: 'center', justifyContent: 'flex-start' }}>
                <Typography 
                    variant='subtitle2' 
                    color={roundValue === number ? `${appTheme.palette.secondary.main}` : `${appTheme.palette.primary.main}`} 
                    sx={{ 
                        textAlign: 'center',
                        fontWeight: 500
                    }}>
                        {number}
                    </Typography>
            </Stack>
        </IconButton>
    )
}